"use client";

import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { Spinner } from "@/components/ui/spinner";
import { PageHeader } from "@/components/page-header";
import { supabase } from "@/lib/supabase";
import { ArchiveRestoreIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

type ArchivedAgent = {
  id: string;
  name: string;
  nickname: string | null;
  user_id: string;
  owner_name: string | null;
  archived_at: string;
  archived_by: string | null;
  archived_by_name: string | null;
  created_at: string;
};

const formatDate = (iso: string | null) => {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
};

async function fetchArchivedAgents(): Promise<ArchivedAgent[]> {
  const { data, error } = await supabase.rpc("list_archived_agents");
  if (error) {
    console.error("[ArchivedAgentsPanel] list archived agents failed:", error);
    return [];
  }
  return (data ?? []) as ArchivedAgent[];
}

export function ArchivedAgentsPanel({ onBack }: { onBack: () => void }) {
  const [rows, setRows] = useState<ArchivedAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingRestore, setPendingRestore] = useState<{ id: string; name: string } | null>(null);
  const [restoring, setRestoring] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setRows(await fetchArchivedAgents());
    setLoading(false);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const handleRestoreAgent = useCallback(async () => {
    if (!pendingRestore || restoring) return;
    setRestoring(true);
    const { error } = await supabase.rpc("unarchive_agent", { p_agent_id: pendingRestore.id });
    setRestoring(false);
    if (error) {
      console.error("[ArchivedAgentsPanel] restore agent failed:", error);
      toast.error(error.message || "Failed to restore agent.");
      return;
    }
    toast.success("Agent restored.");
    setPendingRestore(null);
    await load();
  }, [load, pendingRestore, restoring]);

  const columns = useMemo<DataTableColumn<ArchivedAgent>[]>(
    () => [
      {
        header: "Agent",
        render: (row) => (row.nickname ? `${row.name} (@${row.nickname})` : row.name),
      },
      {
        header: "Owner",
        render: (row) => row.owner_name ?? row.user_id.slice(0, 8),
        width: "w-[180px]",
      },
      {
        header: "Archived",
        render: (row) => formatDate(row.archived_at),
        width: "w-[180px]",
      },
      {
        header: "Archived by",
        render: (row) => row.archived_by_name ?? (row.archived_by ? row.archived_by.slice(0, 8) : "—"),
        width: "w-[180px]",
      },
      {
        header: "",
        width: "w-[110px]",
        align: "right",
        render: (row) => (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={restoring && pendingRestore?.id === row.id}
            onClick={() => setPendingRestore({ id: row.id, name: row.name })}
          >
            {restoring && pendingRestore?.id === row.id ? (
              <Spinner className="size-3.5" />
            ) : (
              <ArchiveRestoreIcon className="size-4" />
            )}
            Restore
          </Button>
        ),
      },
    ],
    [pendingRestore, restoring],
  );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader
        variant="collection"
        title="Archived Agents"
        onBack={onBack}
      />
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
        <div className="mx-auto w-full max-w-4xl">
          <DataTable
            columns={columns}
            data={rows}
            getRowKey={(row) => row.id}
            loading={loading}
            searchFilter={(row, q) =>
              row.name.toLowerCase().includes(q.toLowerCase()) ||
              (row.nickname ?? "").toLowerCase().includes(q.toLowerCase()) ||
              (row.owner_name ?? "").toLowerCase().includes(q.toLowerCase())
            }
            onRefresh={() => void load()}
            emptyMessage="No archived agents."
          />
        </div>
      </div>

      <ConfirmDialog
        open={pendingRestore !== null}
        onOpenChange={(open) => {
          if (!open && !restoring) setPendingRestore(null);
        }}
        title={`Restore "${pendingRestore?.name ?? "agent"}"?`}
        description="The agent will reappear for its owner and everyone it was shared with."
        confirmLabel="Restore"
        variant="default"
        loading={restoring}
        onConfirm={() => void handleRestoreAgent()}
      />
    </div>
  );
}
